// src/redux/slices/documentSlice.jsx
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "../services/endpoint";

// Get assessments list with query parameters support
export const getAssessments = createAsyncThunk(
  "document/getAssessments",
  async (params = { page: 1 }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      // Build URL with query parameters
      let url = `${BASE_URL}/api/assessment/get-list`;
      const queryParams = new URLSearchParams();

      // page parameter
      if (params.page) queryParams.append("page", params.page);

      // date parameters if they exist
      if (params.from_date) queryParams.append("from_date", params.from_date);
      if (params.to_date) queryParams.append("to_date", params.to_date);

      // customer_group_name parameter if it exists
      if (params.customer_group_name) {
        queryParams.append("customer_group_name", params.customer_group_name);
      }

      // Add any other filters that might be needed
      if (params.facility_id) {
        queryParams.append("facility_id", params.facility_id);
      }

      if (params.category_id) {
        queryParams.append("category_id", params.category_id);
      }

      if (params.user_id) {
        queryParams.append("user_id", params.user_id);
      }

      // Append query parameters to URL if they exist
      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || "Failed to fetch assessments");
      }

      return data;
    } catch (err) {
      return rejectWithValue("Network error");
    }
  },
);

// Get assessment details by ID
export const getAssessmentDetails = createAsyncThunk(
  "document/getAssessmentDetails",
  async (assessmentId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${BASE_URL}/api/assessment/get/${assessmentId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(
          data.message || "Failed to fetch assessment details",
        );
      }

      return data;
    } catch (err) {
      return rejectWithValue("Network error");
    }
  },
);

// Delete assessment - USING assessment_id
export const deleteAssessment = createAsyncThunk(
  "document/deleteAssessment",
  async (assessmentId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${BASE_URL}/api/assessment/delete/${assessmentId}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || "Failed to delete assessment");
      }

      return {
        assessmentId,
        success: data.success || true,
        message: data.message || "Assessment deleted successfully",
        data: data,
      };
    } catch (err) {
      return rejectWithValue(err.message || "Network error");
    }
  },
);

// Build a filename matching the backend's naming convention:
// "{Facility Name}-{Category Name}-{YYYY-MM-DD_HH-mm-ss}.pdf"
// This is used as a fallback when the Content-Disposition header is
// not exposed to the browser (cross-origin fetch only exposes
// CORS-safelisted headers unless Access-Control-Expose-Headers is set).
export const buildAssessmentPdfFileName = (assessment = {}) => {
  const original = assessment.originalData || assessment;
  const facility = original.facility_name || assessment.facility_name || "";
  const category =
    original.category_name ||
    assessment.category_name ||
    assessment.documentName ||
    "assessment";
  const created =
    original.created_at ||
    original.upload_date ||
    assessment.created_at ||
    "";

  // Extract the raw date/time parts so we don't depend on timezone parsing.
  // Handles both "2026-08-12 16:14:14" and "2026-08-12T16:14:14..."
  let datePart = "";
  const dateMatch = String(created).match(
    /(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2}):(\d{2})/,
  );
  if (dateMatch) {
    datePart = `${dateMatch[1]}_${dateMatch[2]}-${dateMatch[3]}-${dateMatch[4]}`;
  }

  const base = `${facility}-${category}`
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .trim();
  const safeBase = base || "assessment";

  return datePart ? `${safeBase}-${datePart}.pdf` : `${safeBase}.pdf`;
};

// Download assessment PDF document
export const downloadAssessmentPDF = createAsyncThunk(
  "document/downloadAssessmentPDF",
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      // Accept either a raw assessmentId or the full assessment object
      // so callers can pass the row data for filename reconstruction.
      const assessment =
        payload && typeof payload === "object"
          ? payload
          : { assessmentId: payload };
      const assessmentId =
        assessment.assessmentId ||
        assessment.assessment_id ||
        assessment.originalData?.assessment_id ||
        assessment.originalData?.id ||
        assessment.id;

      const response = await fetch(
        `${BASE_URL}/api/assessment/get-document/${assessmentId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/pdf",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.message || "Failed to download PDF");
      }

      // Get the PDF blob from response
      const pdfBlob = await response.blob();

      // Extract the backend-provided filename from the Content-Disposition
      // header. The browser drops this header when downloading via a blob
      // URL, so we must read it here and set it on the link ourselves.
      const contentDisposition = response.headers.get("Content-Disposition");
      let fileName = null;
      if (contentDisposition) {
        const encodedMatch = contentDisposition.match(
          /filename\*=UTF-8''([^;]+)/i,
        );
        if (encodedMatch && encodedMatch[1]) {
          try {
            fileName = decodeURIComponent(encodedMatch[1].trim());
          } catch {
            // fall through to the plain filename form
          }
        }
        if (!fileName) {
          const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
          if (plainMatch && plainMatch[1]) {
            fileName = plainMatch[1].trim();
          }
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      // Prefer the backend header filename; otherwise reconstruct the
      // backend-style name from the assessment data; last resort: ID name.
      link.download =
        fileName ||
        buildAssessmentPdfFileName(assessment) ||
        `assessment-${assessmentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return {
        success: true,
        message: "PDF downloaded successfully",
        assessmentId,
      };
    } catch (err) {
      return rejectWithValue("Network error while downloading PDF");
    }
  },
);

// Download assessments list CSV
export const downloadAssessmentsCSV = createAsyncThunk(
  "document/downloadAssessmentsCSV",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      // Build URL with query parameters
      let url = `${BASE_URL}/api/assessment/get-list-csv`;
      const queryParams = new URLSearchParams();

      // Add date parameters if they exist
      if (params.from_date) queryParams.append("from_date", params.from_date);
      if (params.to_date) queryParams.append("to_date", params.to_date);

      // Add customer_group_name parameter if it exists
      if (params.customer_group_name) {
        queryParams.append("customer_group_name", params.customer_group_name);
      }

      // Add local_time_difference parameter if it exists
      if (
        params.local_time_difference !== undefined &&
        params.local_time_difference !== null
      ) {
        queryParams.append(
          "local_time_difference",
          params.local_time_difference,
        );
      }

      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json, text/csv", // CHANGED: Accept both JSON and CSV
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.message || "Failed to download CSV");
      }

      // Get response as text first
      const responseText = await response.text();

      // Check if it's the "No data found" JSON response
      try {
        const jsonData = JSON.parse(responseText);
        if (
          jsonData.success === true &&
          jsonData.message &&
          jsonData.message.includes("No data found")
        ) {
          return rejectWithValue(jsonData.message);
        }
      } catch {
        // Not JSON, must be CSV - proceed with download
      }

      // Convert text back to blob for CSV download
      const blob = new Blob([responseText], { type: "text/csv" });
      return blob;
    } catch (err) {
      return rejectWithValue("Network error");
    }
  },
);

const documentSlice = createSlice({
  name: "document",
  initialState: {
    assessments: {
      data: [],
      current_page: 1,
      total: 0,
      per_page: 10,
      last_page: 1,
    },
    assessmentDetails: null,
    isLoading: false,
    isDetailsLoading: false,
    isDeleting: false,
    isDownloadingPDF: false,
    error: null,
    detailsError: null,
    deleteError: null,
    downloadPDFError: null,
    isDownloadingCSV: false,
    downloadCSVError: null,
    successMessage: "",
    deleteSuccess: false,
  },
  reducers: {
    clearDocumentState: (state) => {
      state.error = null;
      state.detailsError = null;
      state.deleteError = null;
      state.downloadPDFError = null;
      state.successMessage = "";
      state.downloadCSVError = null;
      state.deleteSuccess = false;
    },
    clearAssessments: (state) => {
      state.assessments = {
        data: [],
        current_page: 1,
        total: 0,
        per_page: 10,
        last_page: 1,
      };
      state.error = null;
    },
    clearAssessmentDetails: (state) => {
      state.assessmentDetails = null;
      state.detailsError = null;
    },
  },
  extraReducers: (builder) => {
    // Get Assessments
    builder
      .addCase(getAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAssessments.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success) {
          state.assessments = action.payload.data || action.payload;
        } else {
          state.error = action.payload.message || "Failed to fetch assessments";
        }
      })
      .addCase(getAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch assessments";
      });

    // Get Assessment Details
    builder
      .addCase(getAssessmentDetails.pending, (state) => {
        state.isDetailsLoading = true;
        state.detailsError = null;
      })
      .addCase(getAssessmentDetails.fulfilled, (state, action) => {
        state.isDetailsLoading = false;
        if (action.payload.success) {
          state.assessmentDetails = action.payload.data;
        } else {
          state.detailsError =
            action.payload.message || "Failed to fetch assessment details";
        }
      })
      .addCase(getAssessmentDetails.rejected, (state, action) => {
        state.isDetailsLoading = false;
        state.detailsError =
          action.payload || "Failed to fetch assessment details";
      });

    // Delete Assessment - USING assessment_id
    builder
      .addCase(deleteAssessment.pending, (state) => {
        state.isDeleting = true;
        state.deleteError = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteAssessment.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.deleteSuccess = true;
        state.successMessage =
          action.payload.message || "Assessment deleted successfully";

        // Remove the deleted assessment from the state using assessment_id
        state.assessments.data = state.assessments.data.filter(
          (assessment) =>
            assessment.assessment_id !== action.payload.assessmentId,
        );

        // Update total count
        state.assessments.total = Math.max(0, state.assessments.total - 1);
      })
      .addCase(deleteAssessment.rejected, (state, action) => {
        state.isDeleting = false;
        state.deleteError = action.payload || "Failed to delete assessment";
        state.deleteSuccess = false;
      });

    // Download Assessment PDF
    builder
      .addCase(downloadAssessmentPDF.pending, (state) => {
        state.isDownloadingPDF = true;
        state.downloadPDFError = null;
      })
      .addCase(downloadAssessmentPDF.fulfilled, (state, action) => {
        state.isDownloadingPDF = false;
        state.successMessage =
          action.payload.message || "PDF downloaded successfully";
      })
      .addCase(downloadAssessmentPDF.rejected, (state, action) => {
        state.isDownloadingPDF = false;
        state.downloadPDFError = action.payload || "Failed to download PDF";
      });

    builder
      .addCase(downloadAssessmentsCSV.pending, (state) => {
        state.isDownloadingCSV = true;
        state.downloadCSVError = null;
      })
      .addCase(downloadAssessmentsCSV.fulfilled, (state, action) => {
        state.isDownloadingCSV = false;
        // Trigger file download
        const url = window.URL.createObjectURL(action.payload);
        const link = document.createElement("a");
        link.href = url;
        link.download = `assessments-${
          new Date().toISOString().split("T")[0]
        }.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        state.successMessage = "CSV downloaded successfully";
      })
      .addCase(downloadAssessmentsCSV.rejected, (state, action) => {
        state.isDownloadingCSV = false;
        state.downloadCSVError = action.payload;
      });
  },
});

export const { clearDocumentState, clearAssessments, clearAssessmentDetails } =
  documentSlice.actions;
export default documentSlice.reducer;
